<template>
    <div data-testid="glselection-filter" class="d-flex justify-center">
        <div class="gemini-loader-container">
            <div class="border-on d-flex shadow-on radius-small cycle-filter-bg d-flex align-center gap-2">
                <!-- Period Filter -->
                <div class="filter-period d-flex flex-column gap-2 px-3 py-2 hoverable">
                    <div class="grey--text text--darken-1 line-1 noTransform" :data-testid="`glselection-filter__period-label`">
                        {{ $nova.labelsName('gl_selection', 'month_label', 'Period') }}
                    </div>
                    <div class="font-weight-bold">
                        <v-menu offset-y open-on-hover :close-delay="200" transition="slide-y-transition" :close-on-content-click="false" content-class="variance-analysis-filter-menu-content">
                            <template #activator="{ on, attrs }">
                                <div class="filter-hide cursor-pointer noTransform" v-bind="attrs" v-on="on"
                                    data-testid="glselection-filter__period-activator">
                                    <span class="filter-text" :title="formattedPeriod">
                                        {{ formattedPeriod }}
                                    </span>
                                    <img src="/img/icons/downArrow.svg" alt="Down arrow icon" class="arrow-icon">
                                </div>
                            </template>
                            <v-date-picker v-model="localSelectedPeriodToGet" type="month" no-title scrollable
                                :min="minDate" :max="selectedPeriodToGetDefult" color="primary">
                        </v-date-picker>
                        </v-menu>
                    </div>
                </div>

                <div class="filter-divider"></div>

                <!-- Network Filter -->
                <div class="filter-network d-flex flex-column gap-2 px-3 py-2 hoverable">
                    <div class="grey--text text--darken-1 line-1 noTransform" :data-testid="`glselection-filter__network-label`">
                        {{ `${$nova.labelsName('access_management', 'division_label', 'Network')}` }}
                    </div>
                    <div class="font-weight-bold">
                        <v-menu offset-y open-on-hover :close-delay="200" :close-on-content-click="false" transition="slide-y-transition" content-class="variance-analysis-filter-menu-content">
                            <template #activator="{ on, attrs }">
                                <div class="filter-hide cursor-pointer noTransform" v-bind="attrs" v-on="on"
                                    data-testid="glselection-filter__network-activator">
                                    <span class="filter-text" :title="selectedNetworkName">
                                        {{ selectedNetworkName }}
                                    </span>
                                    <img src="/img/icons/downArrow.svg" alt="Down arrow icon" class="arrow-icon">
                                </div>
                            </template>
                            <v-list dense class="pa-0 scrollbar-list filter-list-container">
                                <div class="px-2 pt-2 pb-1 sticky-top bg-white filter-sticky-header">
                                    <v-text-field
                                        v-model="networkSearch"
                                        placeholder="Search"
                                        dense
                                        outlined
                                        hide-details
                                        class="font-size-sm" 
                                        @click.stop
                                    ></v-text-field>
                                </div>
                                <v-divider></v-divider>
                                <v-list-item v-for="item in filteredNetworks" :key="item._id" @click="localSelectedNetwork = item._id">
                                    <span class="filter-list-item-title">{{ item.name }}</span>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </div>
                </div>

                <div class="filter-divider"></div>

                <!-- Cycle Type Filter -->
                <div class="filter-cycletype d-flex flex-column gap-2 px-3 py-2 hoverable">
                    <div class="grey--text text--darken-1 line-1 noTransform" :data-testid="`glselection-filter__cycle-type-label`">
                        Cycle Type
                    </div>
                    <div class="font-weight-bold">
                        <v-menu offset-y open-on-hover :close-delay="200" transition="slide-y-transition" content-class="variance-analysis-filter-menu-content">
                            <template #activator="{ on, attrs }">
                                <div class="filter-hide cursor-pointer noTransform" v-bind="attrs" v-on="on"
                                    data-testid="glselection-filter__cycle-type-activator">
                                    <span class="filter-text" :title="selectedCycleTypeName">
                                        {{ selectedCycleTypeName }}
                                    </span>
                                    <img src="/img/icons/downArrow.svg" alt="Down arrow icon" class="arrow-icon">
                                </div>
                            </template>
                            <v-list dense class="pa-0 scrollbar-list">
                                <v-list-item v-for="item in cycletypeList" :key="item.value"
                                    @click="localCycleType = item.value">
                                    <span class="filter-list-item-title">{{ item.name }}</span>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </div>
                </div>

                <div class="filter-divider"></div>

                <v-btn data-testid="glselection-filter__apply-btn" depressed
                    :color="hasPendingApply ? 'primary' : 'grey lighten-3'" :class="[
                        '',
                        hasPendingApply ? 'white--text' : 'grey--text text--darken-1'
                    ]" :disabled="!hasPendingApply"
                    @click="applyFilters">Apply
                </v-btn>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'GlSelectionFilter',
    props: {
        selectednetwork: {
            type: String,
            default: null
        },
        data: {
            type: Array,
            default: () => []
        },
        cycletype: {
            type: String,
            default: null
        },
        cycletypeList: {
            type: Array,
            default: () => []
        },
        selectedPeriodToGet: {
            type: String,
            default: null
        },
        selectedPeriodToGetDefult: {
            type: String,
            default: null
        },
        minDate: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            localSelectedNetwork: this.selectednetwork,
            localCycleType: this.cycletype,
            localSelectedPeriodToGet: this.selectedPeriodToGet,
            networkSearch: '',
        }
    },
    watch: {
        selectednetwork(val) {
            this.localSelectedNetwork = val;
        },
        cycletype(val) {
            this.localCycleType = val;
        },
        selectedPeriodToGet(val) {
            this.localSelectedPeriodToGet = val;
        }
    },
    computed: {
        filteredNetworks() {
            if (!this.networkSearch) return this.data;
            const search = this.networkSearch.toLowerCase();
            return this.data.filter(item => 
                (item.name || '').toLowerCase().includes(search)
            );
        },
        hasPendingApply() {
            return this.localSelectedNetwork !== this.selectednetwork ||
                this.localCycleType !== this.cycletype ||
                this.localSelectedPeriodToGet !== this.selectedPeriodToGet;
        },
        selectedNetworkName() {
            const found = this.data.find(n => n._id === this.localSelectedNetwork);
            return found ? found.name : 'Select Network';
        },
        selectedCycleTypeName() {
            const found = this.cycletypeList.find(c => c.value === this.localCycleType);
            return found ? found.name : 'Select Cycle Type';
        },
        formattedPeriod() {
            if (!this.localSelectedPeriodToGet) return '';
            const date = new Date(this.localSelectedPeriodToGet + '-01');
            return date.toLocaleString('default', { month: 'long', year: 'numeric' });
        }
    },
    methods: {
        applyFilters() {
            this.$emit('update:selectednetwork', this.localSelectedNetwork);
            this.$emit('update:cycletype', this.localCycleType);
            this.$emit('update:selectedPeriodToGet', this.localSelectedPeriodToGet);
            this.$emit('refresh-data');
        }
    }
}
</script>
<style scoped>
/* Fixed width for filters */
.filter-period {
    width: 170px !important;
    max-width: 170px !important;
}

.filter-network {
    width: 150px !important;
    max-width: 150px !important;

}

.filter-cycletype {
    width: 150px !important;
    max-width: 150px !important;
}

/* Hover effects matching Filter.vue */
.hoverable:hover {
    background-color: var(--cycle-filter-section-bg) !important;
    border-radius: var(--radius-8);
}

:deep(.hoverable):hover .arrow-icon {
    transform: rotate(180deg);
}

.arrow-icon {
    transition: transform 0.3s ease;
}

/* Text styling matching Filter.vue */
.hoverable .grey--text {
    color: #64748b !important;
    font-weight: 500;
    text-transform: none !important;
    letter-spacing: normal !important;
    font-size: var(--font-size-xxs);
}

.hoverable .font-weight-bold {
    color: #0f172a !important; /* Slate-900 for values */
    font-weight: 600 !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
}

.v-list-item:hover {
  background-color: var(--cycle-filter-section-bg) !important;
}

.filter-hide {
    display: flex;
    align-items: center;
    gap: 4px;
    justify-content: space-between;
    width: 100%;
}

.filter-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    flex: 1;
    text-align: left;
}


/* Restore container styling */
.cycle-filter-bg {
    background-color: #ffffff !important;
    border: 1px solid #e2e8f0 !important;
    border-radius: 8px !important;
    padding: 8px 16px;
    box-shadow: 0px 4px 4px 0px #00000014 !important;
}

.filter-divider {
    display: block;
    height: 36px;
    width: 1px;
    background-color: #cbcbcc;
}

.apply-btn {
    border-radius: 6px;
    font-weight: 500;
    letter-spacing: normal;
    text-transform: none !important;
}

/* Gemini AI Style Loader - Traversing Border Animation */
.gemini-loader-container {
    padding: 2px; /* Added padding for border visibility */
    position: relative;
    border-radius: 10px;
    background: transparent;
    transition: box-shadow 0.3s ease;
}

.gemini-loader-container.loading {
    animation: gemini-shadow-pulse 2s ease-in-out infinite;
}

.gemini-loader-container.loading::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 10px;
    padding: 2px;
    background: linear-gradient(90deg,
            #3b82f6,
            #8b5cf6,
            #ec4899,
            #3b82f6);
    background-size: 400% 100%;
    -webkit-mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    animation: gemini-border-flow 3s linear infinite;
    pointer-events: none;
}

@keyframes gemini-border-flow {
    0% {
        background-position: 0% 50%;
    }

    100% {
        background-position: 100% 50%;
    }
}

@keyframes gemini-shadow-pulse {

    0%,
    100% {
        box-shadow: 0 0 5px rgba(59, 130, 246, 0.3);
    }

    50% {
        box-shadow: 0 0 15px rgba(139, 92, 246, 0.5);
    }
}

.v-menu__content {
    z-index: 9999 !important;
}

.filter-list-container {
    max-height: 300px;
    overflow-y: auto;
}

.filter-sticky-header {
    position: sticky;
    top: 0;
    z-index: 1;
    background: var(--grey-white);
}
</style>
